import { getExtraHours, calcExtraHoursTotal } from "./extraHoursBilling";
import React, { useEffect, useMemo, useState } from "react";
import Axios from "axios";
import "./ReportesWeb.css";

/* ================== Axios base ================== */
Axios.defaults.baseURL = "https://backend.sansfrontieres-design.com";
Axios.defaults.withCredentials = false;

/* Mismo filtro del Resumen */
const EXCLUDED_STATUS_FOR_XLS = new Set(["billing", "payed", "archived", "pre-archived"]);
const filterOutExcludedXLS = (rows) =>
  (Array.isArray(rows) ? rows : []).filter((r) => {
    const s = String(r?.jobsta ?? "").trim().toLowerCase();
    return !EXCLUDED_STATUS_FOR_XLS.has(s);
  });

const normalize = (v) => (v ?? "").toString().toLowerCase().replace(/[\s-]+/g, "");

const n = (v) => {
  const x = parseFloat(String(v ?? "").replace(",", "."));
  return isNaN(x) ? 0 : x;
};

const r2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

// Equivalencias de Type para cálculos de reportes.
// Solo cambia cómo se interpreta el Type al calcular; no altera el valor guardado en BD.
const normalizeCalcJobType = (value) => {
  const t = normalize(value);
  switch (t) {
    case "coaxbentleyasb": return "coaxasbuilt";
    case "coaxbentleydes": return "coaxdesign";
    case "fiberbentleyasb": return "fiberasbuiltbau";
    case "fiberbentleydes": return "fiberasbuiltbau";
    default: return t;
  }
};


// Types que NO cobran LODE base en la fórmula genérica de Main/Main-SFD.
// Además, el recargo genérico por LODE > 2000 fue eliminado para TODOS los Types.
const NO_GENERIC_LODE_BASE_TYPES = new Set([
  "coaxasbuilt",
  "coaxdesign",
  "coaxbentleyasb",
  "coaxbentleydes",
  "fiberbentleyasb",
  "fiberbentleydes",
  "route",
  "routefttp",
  "fiberfttp",
]);

const usesGenericLodeBase = (r) => !NO_GENERIC_LODE_BASE_TYPES.has(normalize(r?.jobti));

const isBentleySplitCoaxAsbuilt = (r) => normalize(r?.jobti) === "bentleysplitcoaxasbuilt";
const isBentleySplitFiberAsbuilt = (r) => normalize(r?.jobti) === "bentleysplitfiberasbuilt";

const isProject01 = (r) => String(r?.jobpro ?? "").trim() === "Project-01";
const isFiberDesignBAU = (r) => normalize(r?.jobti) === "fiberdesignbau";
const isFiberAsbuiltBAU = (r) => {
  const t = normalize(r?.jobti);
  return t === "fiberasbuiltbau" || t === "fiberbentleyasb";
};
const isCoaxAsbuilt = (r) => {
  const t = normalize(r?.jobti);
  return t === "coaxasbuilt" || t === "coaxbentleyasb";
};

// Route / Route FTTP: SOLO procesan Route.
// NO Coax, NO Asbuilt, NO LODE, NO HC.
const isRouteOnlyType = (r) => {
  const t = normalize(r?.jobti);
  return t === "route" || t === "routefttp";
};

const isCoaxAsbRevertSplit = (r) => normalize(r?.jobti) === "coaxasbrevertsplit";
const isCoaxAsbRevertN0 = (r) => normalize(r?.jobti) === "coaxasbrevertn+0";
const isIrisSplitCoaxAsbuilt = (r) => normalize(r?.jobti) === "irissplitcoaxasbuilt";

// Coax Asb Revert Split - regla nueva:
// Main = 160 fijo.
// Main-SFD = 12.5 + $0.50 por cada HC/AMP por encima de 20.
const coaxAsbRevertSplitSfdTotal = (hc) => {
  const value = n(hc);
  return r2(12.5 + Math.max(0, value - 20) * 0.5);
};

// Main: devuelve AL (suma AC..AK) según modo
// Add the separately priced hours exactly once, preserving every base-price rule.
function calcMainAL(r, mode) {
  return r2(calcMainBase(r, mode) + calcExtraHoursTotal(r, mode === "ADV" ? "SFD" : "CLIENT"));
}
function calcBentleyTotal(r, mode) {
  return r2(calcBentleyBase(r, mode) + calcExtraHoursTotal(r, mode === "BENTLEY_SFD" ? "SFD" : "CLIENT"));
}

function calcMainBase(r, mode /* "SFD" | "ADV" */) {
  const routeA = n(r?.jobrouteaerial);
  const routeU = n(r?.jobrouteug);
  const coaxA = n(r?.jobcoaxae);
  const coaxU = n(r?.jobcoaxug);
  const asbA = n(r?.jobasbae);
  const asbU = n(r?.jobasbug);
  const lode = n(r?.joblode);
  const hc = n(r?.jobhc);
  const jobType = normalizeCalcJobType(r?.jobti);

  // Reglas específicas de Coax Asb Revert.
  // Se evalúan antes de Project-01 porque sus precios son propios y no dependen de la regla genérica.
  if (isCoaxAsbRevertSplit(r)) {
    if (mode === "ADV") return coaxAsbRevertSplitSfdTotal(hc);
    return r2(160);
  }

  // Coax Asb Revert N+0: precio total fijo, sin condiciones ni componentes adicionales.
  if (isCoaxAsbRevertN0(r)) {
    if (mode === "ADV") return r2(10);
    return r2(20);
  }

  // Iris Split Coax Asbuilt:
  // Main = 105 fijo.
  // Main-SFD = misma regla que Coax Asb Revert Split.
  if (isIrisSplitCoaxAsbuilt(r)) {
    if (mode === "ADV") return coaxAsbRevertSplitSfdTotal(hc);
    return r2(105);
  }

  if (mode === "ADV" && isProject01(r)) return 0;

  // Route / Route FTTP - regla exclusiva de Route:
  // MAIN: SOLO Route(Main). NO Coax, NO Asbuilt, NO LODE, NO HC.
  // MAIN-SFD: SOLO Route(Main-SFD). NO Coax, NO Asbuilt, NO LODE, NO HC.
  if (isRouteOnlyType(r)) {
    const totalRoute = routeA + routeU;

    if (mode === "ADV") {
      if (totalRoute > 20000) return r2(totalRoute * 0.008);
      let routeTotal = totalRoute > 0 ? r2(10) : 0;
      if (totalRoute > 2000) routeTotal += r2(((totalRoute - 2000) / 5280) * 12.5);
      return r2(routeTotal);
    }

    if (totalRoute > 20000) return r2(totalRoute * 0.008);
    let routeTotal = totalRoute > 0 ? r2(30.25) : 0;
    if (totalRoute > 2000) routeTotal += r2((totalRoute - 2000) * 0.008);
    return r2(routeTotal);
  }

  // Coax Asbuilt / Coax Bentley Asb - regla vigente:
  // MAIN: SOLO Total Asbuilt. NO Route, NO Coax, NO LODE, NO HC.
  // MAIN-SFD: SOLO Total Asbuilt. NO Route, NO Coax, NO LODE, NO HC.
  if (isCoaxAsbuilt(r)) {
    const totalAsb = asbA + asbU;

    if (mode === "ADV") {
      const asbuiltBase = totalAsb > 0 ? r2(8.5) : 0;
      const asbuiltGt2000 = totalAsb > 2000
        ? r2(((totalAsb - 2000) / 5280) * 10)
        : 0;
      return r2(asbuiltBase + asbuiltGt2000);
    }

    const asbuiltBase = totalAsb > 0 ? r2(25) : 0;
    const asbuiltGt2000 = totalAsb > 2000
      ? r2((totalAsb - 2000) * 0.014)
      : 0;
    return r2(asbuiltBase + asbuiltGt2000);
  }

  // Fiber Design BAU - reglas especiales:
  // MAIN: 55.75 + Route(Main) + LODE/Nodes*14.25 + HC/Amps*16.25 + Coax>2000*0.019
  // MAIN-SFD: 12.50 + Route(Main-SFD) + LODE*5 + HC*5 + $10 por milla de Coax sobre 2000'
  if (isFiberDesignBAU(r)) {
    const totalRoute = routeA + routeU;
    const totalCoax = coaxA + coaxU;
    const coaxGt2000Component = totalCoax > 2000
      ? (mode === "SFD"
          ? r2((totalCoax - 2000) * 0.019)
          : r2(((totalCoax - 2000) / 5280) * 10))
      : 0;
    let routeComponent = 0;

    if (mode === "SFD") {
      if (totalRoute > 20000) {
        routeComponent = r2(totalRoute * 0.008);
      } else {
        if (totalRoute > 0) routeComponent += r2(30.25);
        if (totalRoute > 2000) routeComponent += r2((totalRoute - 2000) * 0.008);
      }

      return r2(
        55.75 +
        routeComponent +
        (lode * 14.25) +
        (hc * 16.25) +
        coaxGt2000Component
      );
    }

    // ADV / Main-SFD
    if (totalRoute > 20000) {
      routeComponent = r2(totalRoute * 0.008);
    } else {
      if (totalRoute > 0) routeComponent += r2(10);
      if (totalRoute > 2000) {
        routeComponent += r2(((totalRoute - 2000) / 5280) * 12.5);
      }
    }

    return r2(
      12.5 +
      routeComponent +
      (lode * 5) +
      (hc * 5) +
      coaxGt2000Component
    );
  }

  // Fiber Asbuilt BAU / Fiber Bentley Asb - regla vigente:
  // MAIN: 25 + HC/Amps*16.25 + Asbuilt>2000 * 0.014
  // MAIN-SFD: 15 + HC/Amps*5 + $10 por milla de Asbuilt sobre 2000'
  // NO considera Route, Coax ni LODE.
  if (isFiberAsbuiltBAU(r)) {
    const totalAsb = asbA + asbU;

    if (mode === "SFD") {
      const asbuiltGt2000 = totalAsb > 2000
        ? r2((totalAsb - 2000) * 0.014)
        : 0;
      return r2(
        25 +
        (hc * 16.25) +
        asbuiltGt2000
      );
    }

    const asbuiltGt2000 = totalAsb > 2000
      ? r2(((totalAsb - 2000) / 5280) * 10)
      : 0;
    return r2(
      15 +
      (hc * 5) +
      asbuiltGt2000
    );
  }

  // Bentley Split Coax Asbuilt: Main = 105; Main-SFD usa la misma lógica de Iris-Splits.
  if (isBentleySplitCoaxAsbuilt(r)) {
    if (mode === "ADV") return calcBentleyBase(r, "BENTLEY_SFD");
    return r2(105);
  }

  // Bentley Split Fiber Asbuilt:
  // Main = 105 fijo.
  // Main-SFD = regla de Fiber Iris SOLO para Main-SFD.
  if (isBentleySplitFiberAsbuilt(r)) {
    if (mode === "ADV") {
      return r2(30 + Math.max(0, lode - 1) * 15);
    }
    return r2(105);
  }

  if (jobType === "fiberiris" || jobType === "irissplitfiberdesign" || jobType === "irissplitfiberasbuilt" || jobType === "bentleysplitfiberdesign") {
    if (lode <= 0) return 0;
    if (mode === "ADV") return r2(30 + Math.max(0, lode - 1) * 15);
    return r2(60 + Math.max(0, lode - 1) * 33);
  }

  const totalRoute = routeA + routeU;
  const totalCoax = coaxA + coaxU;
  const totalAsb = asbA + asbU;

  let totalStrand = 0;      // AC
  let strandGt2000 = 0;     // AD
  let strandGt20000 = 0;    // AE
  let totalDesign = 0;      // AF
  let designGt2000 = 0;     // AG
  let lodeBase = 0;         // AH
  let lodeGt2000 = 0;       // AI
  let asbuiltBase = 0;      // AJ
  let asbuiltGt2000 = 0;    // AK

  if (mode === "ADV") {
    if (totalRoute > 0) totalStrand = r2(10);
    if (totalCoax > 0) totalDesign = r2(12.5);
    if (totalAsb > 0) asbuiltBase = r2(8.5);
    if (lode > 0 && usesGenericLodeBase(r)) lodeBase = r2(8.5);

    if (totalRoute > 2000) strandGt2000 = r2(((totalRoute - 2000) / 5280) * 12.5);

    if (totalRoute > 20000) {
      totalStrand = 0;
      strandGt2000 = 0;
      strandGt20000 = r2(totalRoute * 0.008);
    }

    if (totalCoax > 2000) designGt2000 = r2(((totalCoax - 2000) / 5280) * 15.5);
    if (totalAsb > 2000) asbuiltGt2000 = r2(((totalAsb - 2000) / 5280) * 10);
    lodeGt2000 = 0; // Regla vigente: NO existe adicional genérico por LODE > 2000.
  } else {
    totalStrand = totalRoute > 0 ? r2(30.25) : 0;
    strandGt2000 = totalRoute > 2000 ? r2((totalRoute - 2000) * 0.008) : 0;
    strandGt20000 = 0;

    if (totalRoute > 20000) {
      totalStrand = 0;
      strandGt2000 = 0;
      strandGt20000 = r2(totalRoute * 0.008);
    }

    totalDesign = totalCoax > 0 ? r2(36.25) : 0;
    designGt2000 = totalCoax > 2000 ? r2((totalCoax - 2000) * 0.014) : 0;

    lodeBase = (lode > 0 && usesGenericLodeBase(r)) ? r2(15) : 0;
    lodeGt2000 = 0; // Regla vigente: NO existe adicional genérico por LODE > 2000.

    asbuiltBase = totalAsb > 0 ? r2(25) : 0;
    asbuiltGt2000 = totalAsb > 2000 ? r2((totalAsb - 2000) * 0.014) : 0;
  }

  return r2(
    totalStrand +
      strandGt2000 +
      strandGt20000 +
      totalDesign +
      designGt2000 +
      lodeBase +
      lodeGt2000 +
      asbuiltBase +
      asbuiltGt2000
  );
}

// Coax Bentley: devuelve TOTAL (columna AC/TOTAL) según modo
function calcBentleyBase(r, mode /* "SFD" | "BENTLEY_SFD" */) {
  const lode = n(r?.joblode);

  if (mode === "BENTLEY_SFD") {
    if (isProject01(r)) return 0;

    const project = String(r?.jobpro ?? "");
    const rawW = r?.joblode;
    const rawX = r?.jobhc;

    const isBlankW = rawW === "" || rawW === null || rawW === undefined;
    if (isBlankW) return 0;

    const W = parseFloat(String(rawW).replace(",", "."));
    const Xs = String(rawX ?? "");

    const isXEmptyOrZeroOrDash = Xs === "" || Xs === "-" || Xs === "0" || Xs === "0.0";

    const X = parseFloat(String(rawX ?? "").replace(",", "."));
    const xNum = isNaN(X) ? 0 : X;

    const calcFromX = () => {
      if (isXEmptyOrZeroOrDash) return 15;
      if (xNum <= 14) return 30;
      if (xNum <= 24) return 45;
      return 60;
    };

    if (project === "Project-02" || project === "Project-03") {
      if (W === 2) return 60;
      if (W === 1) return calcFromX();
      return 0;
    }

    if (W >= 1) return calcFromX();
    return 0;
  }

  // SFD (viejo)
  if (lode === 0) return 0;
  if (lode === 1) return 125 + 55;
  if (lode > 1) return (125 * lode) + 55;
  return 0;
}


export default function ReportesWeb() {
  const [projectsRows, setProjectsRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");

  const flags = useMemo(() => ({ main: true, bentley: true, mainSFD: true, bentleySFD: true }), []);
  const isCoaxBentley = (r) => {
    const jobti = normalize(r?.jobti);
    return jobti === "coaxbentley" || jobti === "irissplits" || jobti === "irissplitcoaxdesign" || jobti === "bentleysplitcoaxdesign";
  };

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await Axios.get("/get_projects.php?include_extra_hours=1&_=" + Date.now());
      if (data?.status === "success") {
        setProjectsRows(Array.isArray(data.data) ? data.data : []);
        setUpdatedAt(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
      } else {
        setErr("No se pudo cargar.");
      }
    } catch (e) {
      setErr("Error conexión.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = window.setInterval(load, 60000);
    return () => window.clearInterval(id);
  }, []);

  const summaryBaseRows = useMemo(() => filterOutExcludedXLS(projectsRows), [projectsRows]);
  const resumen = useMemo(() => {
    const arr = summaryBaseRows;
    const rowsBentley = arr.filter((r) => isCoaxBentley(r));
    const rowsMain = arr.filter((r) => !isCoaxBentley(r));

    const sumMain = flags.main ? r2(rowsMain.reduce((acc, r) => acc + calcMainAL(r, "SFD"), 0)) : 0;
    const sumMainSFD = flags.mainSFD ? r2(rowsMain.reduce((acc, r) => acc + calcMainAL(r, "ADV"), 0)) : 0;
    const sumBentley = flags.bentley ? r2(rowsBentley.reduce((acc, r) => acc + calcBentleyTotal(r, "SFD"), 0)) : 0;
    const sumBentleySFD = flags.bentleySFD
      ? r2(rowsBentley.reduce((acc, r) => acc + calcBentleyTotal(r, "BENTLEY_SFD"), 0))
      : 0;

    const baseTotal = r2(sumMain + sumBentley);
    const sfdTotal = r2(sumMainSFD + sumBentleySFD);
    const hasBaseSelection = !!(flags.main || flags.bentley);
    const totalFinal = hasBaseSelection ? (baseTotal > 0 ? r2(baseTotal - sfdTotal) : baseTotal) : sfdTotal;

    const rows = [];
    if (flags.main) rows.push({ concepto: "Main", total: sumMain });
    if (flags.bentley) rows.push({ concepto: "Coax Bentley", total: sumBentley });
    if (flags.mainSFD) rows.push({ concepto: "Main-SFD", total: sumMainSFD });
    if (flags.bentleySFD) rows.push({ concepto: "Coax Bentley SFD", total: sumBentleySFD });

    return { rows: rows.filter((r) => Number(r.total || 0) > 0), totalFinal };
  }, [summaryBaseRows, flags]);

  const fmt = (x) => (Number(x) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch {}
  };

  return (
    <main className="rweb-page">
      <section className="rweb-panel" aria-label="Resumen de reportes">
        <header className="rweb-head">
          <span>Resumen</span>
          <div className="rweb-actions">
            <button type="button" onClick={toggleFullscreen} title="Pantalla completa" aria-label="Pantalla completa">⛶</button>
            <button type="button" onClick={load} disabled={loading} title="Actualizar" aria-label="Actualizar">↻</button>
          </div>
        </header>
        {err && <div className="rweb-message rweb-error" role="alert">{err}</div>}
        {loading && !projectsRows.length && <div className="rweb-message">Cargando…</div>}
        <div className="rweb-totalBox">
          <div className="rweb-totalLabel">TOTAL FINAL</div>
          <div className="rweb-totalValue">{fmt(resumen.totalFinal)}</div>
        </div>
        <div className="rweb-list">
          {resumen.rows.map((row) => (
            <div className="rweb-row" key={row.concepto}>
              <span>{row.concepto}</span>
              <strong>{fmt(row.total)}</strong>
            </div>
          ))}
        </div>
        <footer className="rweb-foot">
          {loading ? "Actualizando…" : updatedAt ? `Act. ${updatedAt}` : ""}
        </footer>
      </section>
    </main>
  );
}
